import React, { useState, useEffect, useRef } from 'react';
import { Package, Plus, FileText, FileSpreadsheet, MoreVertical, Download, Upload } from 'lucide-react';
import CardBasic from '../../ui/Card.jsx';
import ArticlesTabs from './ArticlesTabs.jsx';
import CreateArticleModal from './CreateArticleModal.jsx';
import ImportArticlesModal from './ImportArticlesModal.jsx';
import ArticleEdit from './ArticleEdit.jsx';

const ArticlesIndex = () => {
    const [activeTab, setActiveTab] = useState('articles');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [triggerAddCategory, setTriggerAddCategory] = useState(false);
    const [triggerAddSubCategory, setTriggerAddSubCategory] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [editArticleId, setEditArticleId] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    // Check URL for edit parameter and handle browser back/forward
    useEffect(() => {
        const handleUrlChange = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const editId = urlParams.get('edit');
            if (editId) {
                setEditArticleId(parseInt(editId));
            } else {
                setEditArticleId(null);
            }
        };

        // Initial check on mount
        handleUrlChange();

        // Listen for browser back/forward navigation
        window.addEventListener('popstate', handleUrlChange);

        return () => {
            window.removeEventListener('popstate', handleUrlChange);
        };
    }, []);

    // Callback to track which tab is active
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
    };

    const handleCreateArticle = () => {
        setIsCreateModalOpen(true);
    };

    const handleArticleCreated = () => {
        setRefreshKey(prev => prev + 1);
    };

    const handleEditArticle = (articleId) => {
        setEditArticleId(articleId);
        // Update URL without reload
        window.history.pushState({}, '', `index.php/articles?edit=${articleId}`);
    };

    const handleBackFromEdit = () => {
        setEditArticleId(null);
        setRefreshKey(prev => prev + 1);
        // Update URL without reload
        window.history.pushState({}, '', 'index.php/articles');
    };

    const renderButtons = () => {
        return (
            <div style={{ display: 'flex', gap: '0.75rem', position: 'relative' }}>
                {/* Button: Neue Kategorie (nur bei Categories Tab) */}
                {activeTab === 'categories' && (
                    <button
                        onClick={() => setTriggerAddCategory(prev => !prev)}
                        className="btn btn-outline-primary d-flex align-items-center gap-2 shadow"
                    >
                        <Plus size={20} />
                        Neue Kategorie
                    </button>
                )}

                {/* Button: Neue Unterkategorie (nur bei Subcategories Tab) */}
                {activeTab === 'subCategories' && (
                    <button
                        onClick={() => setTriggerAddSubCategory(prev => !prev)}
                        className="btn btn-outline-primary d-flex align-items-center gap-2 shadow"
                    >
                        <Plus size={20} />
                        Neue Unterkategorie
                    </button>
                )}

                {/* Button: Neuer Artikel (immer sichtbar) */}
                <button
                    onClick={handleCreateArticle}
                    className="btn btn-outline-primary d-flex align-items-center gap-2 shadow"
                >
                    <Plus size={20} />
                    Neuer Artikel
                </button>

                {/* Three Dots Menu for Print Options */}
                <div style={{ position: 'relative' }}>
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="btn btn-outline-secondary d-flex align-items-center shadow"
                        style={{ position: 'relative', padding: '0.625rem 0.875rem' }}
                    >
                        <MoreVertical size={20} />
                    </button>

                    {isMenuOpen && (
                        <>
                            <div
                                style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 999,
                                }}
                                onClick={() => setIsMenuOpen(false)}
                            />
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 0.5rem)',
                                    right: 0,
                                    zIndex: 1000,
                                    minWidth: '220px',
                                    background: 'var(--bg-card, #ffffff)',
                                    border: '1px solid var(--border-medium, #cbd5e1)',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    overflow: 'hidden',
                                }}
                            >
                                <a
                                    href="index.php/docs/article_list"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setIsMenuOpen(false)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '0.75rem 1rem',
                                        textDecoration: 'none',
                                        color: 'var(--text-primary, #2c3e50)',
                                        fontSize: '0.875rem',
                                        transition: 'background-color 0.15s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--bg-hover, #f8fafc)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <FileText size={16} />
                                    Artikelliste drucken
                                </a>
                                <a
                                    href="index.php/docs/article_list_details"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setIsMenuOpen(false)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '0.75rem 1rem',
                                        textDecoration: 'none',
                                        color: 'var(--text-primary, #2c3e50)',
                                        fontSize: '0.875rem',
                                        transition: 'background-color 0.15s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--bg-hover, #f8fafc)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <FileSpreadsheet size={16} />
                                    Artikeldetailliste drucken
                                </a>
                                <div style={{ borderTop: '1px solid var(--border-light, #e2e8f0)', margin: '0.25rem 0' }} />
                                <a
                                    href="index.php/articles/exportArticles"
                                    onClick={() => setIsMenuOpen(false)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '0.75rem 1rem',
                                        textDecoration: 'none',
                                        color: 'var(--text-primary, #2c3e50)',
                                        fontSize: '0.875rem',
                                        transition: 'background-color 0.15s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--bg-hover, #f8fafc)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <Download size={16} />
                                    Artikel exportieren (CSV)
                                </a>
                                <button
                                    onClick={() => { setIsMenuOpen(false); setIsImportModalOpen(true); }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '0.75rem 1rem',
                                        textDecoration: 'none',
                                        color: 'var(--text-primary, #2c3e50)',
                                        fontSize: '0.875rem',
                                        transition: 'background-color 0.15s ease',
                                        background: 'none',
                                        border: 'none',
                                        width: '100%',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--bg-hover, #f8fafc)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <Upload size={16} />
                                    Artikel importieren
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    // If editing an article, show the edit view
    if (editArticleId) {
        return (
            <ArticleEdit
                articleId={editArticleId}
                onBack={handleBackFromEdit}
                onUpdated={handleArticleCreated}
            />
        );
    }

    return (
        <div>
            <div className="mb-4">
                <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                    <div>
                        <h1 className="h2 fw-bold text-dark d-flex align-items-center gap-2 mb-2">
                            <Package size={32} className="icon-articles" />
                            Artikel
                        </h1>
                        <p className="text-muted mb-0">
                            Verwaltung von Artikeln, Kategorien und Unterkategorien
                        </p>
                    </div>
                    {renderButtons()}
                </div>
            </div>
            <CardBasic>
                <ArticlesTabs
                    key={refreshKey}
                    onTabChange={handleTabChange}
                    triggerAddCategory={triggerAddCategory}
                    triggerAddSubCategory={triggerAddSubCategory}
                    onEditArticle={handleEditArticle}
                    onRefreshArticles={handleArticleCreated}
                />
            </CardBasic>

            {/* Create Article Modal */}
            <CreateArticleModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onCreated={handleArticleCreated}
            />

            {/* Import Articles Modal */}
            <ImportArticlesModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onImported={handleArticleCreated}
            />
        </div>
    );
};

export default ArticlesIndex;
